import { motion } from 'framer-motion'
import { ArrowLeft, CrownIcon, Share2Icon } from 'lucide-react'
import { FC, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { FightIcon } from 'assets/icons/fight-icon'
import gameBg1 from 'assets/images/game-bg-rocks.png'
import gameBg2 from 'assets/images/game-bg-sand.png'

import { HexBoard } from 'features/board/components/hex-board'
import { abortLooking, addAIToGame, finishGameByAbort } from 'features/firebase/game-operations'
import { Player } from 'features/firebase/types'
import { useGameEvents } from 'features/firebase/use-game-events'
import { subscribeToUserInfo } from 'features/firebase/user-operations'
import { EndingDialog } from 'features/game/components/ending-dialog'
import { Header } from 'features/game/components/header'
import { PlayerHand } from 'features/game/components/player-hand'
import { RespondDrawDialog } from 'features/game/components/respond-draw-dialog'
import { useGameEventContext } from 'features/game/event-context'
import { useGameContext } from 'features/game/game-context'
import { getGameNumber, isGameOpen } from 'features/game/utils'
import { SVGPlane } from 'features/hex-board/svg-plane/svg-plane'

import { GreenButton, RedButton } from 'shared/components'
import { Button } from 'shared/components/ui/button'
import { Dialog, DialogClose, DialogContent, DialogTitle, DialogTrigger } from 'shared/components/ui/dialog'
import { useCopyToClipboard } from 'shared/hooks/use-copy-to-clipboard'

import distribute from './../distribute.json'

const gameBgs = [gameBg1, gameBg2]

export const Game: FC = () => {
  const copy = useCopyToClipboard()
  const navigate = useNavigate()
  const [topPlayer, setTopPlayer] = useState<Player | null>(null)
  const [bottomPlayer, setBottomPlayer] = useState<Player | null>(null)
  const [showFightAnimation, setShowFightAnimation] = useState(false)
  const { setRawEvents } = useGameEvents()
  const { crownNextPiece, isCrownedPieceOnBoard, isTurnProtection, handleFightClick, handleCrownNextPiece } =
    useGameEventContext()
  const {
    game,
    updateBoardState,
    phase,
    relativeGameData: { playerId, turn, hand, playerColor },
    lastMove,
  } = useGameContext()

  const gameBg = useMemo(() => gameBgs[getGameNumber(game.gameId) % 2], [game.gameId])

  const skip = () => setRawEvents(distribute)

  useEffect(() => {
    if (!game.gameId || !game.autoAddAI) return

    const timeout = Math.random() * 5000 + 10000
    const timer = setTimeout(() => {
      addAIToGame(game.gameId, playerColor.bottom)
    }, timeout)

    if (topPlayer) {
      clearTimeout(timer)
    }

    return () => clearTimeout(timer)
  }, [game.gameId, game.autoAddAI, playerColor.bottom, topPlayer])

  useEffect(() => {
    if (!playerId.top) return

    const unsubscribe = subscribeToUserInfo(playerId.top, player => setTopPlayer(player))
    return unsubscribe
  }, [playerId.top])

  useEffect(() => {
    if (!playerId.bottom) return

    const unsubscribe = subscribeToUserInfo(playerId.bottom, player => setBottomPlayer(player))
    return unsubscribe
  }, [playerId.bottom])

  useEffect(() => {
    if (lastMove && lastMove.type === 'fight') {
      setShowFightAnimation(true)

      const timer = setTimeout(() => {
        setShowFightAnimation(false)
        updateBoardState.current()
      }, 600)

      return () => clearTimeout(timer)
    }
  }, [lastMove, updateBoardState])

  const copyGameId = () => {
    copy(game.gameId)
    toast.success('Game ID copied!')
  }

  const shareGame = () => {
    if (location.hostname === 'localhost') {
      copy(`localhost:3000/play/${game.gameId}`)
    } else {
      copy(`https://elementums.web.app/play/${game.gameId}`)
    }

    toast.success('Link copied!')
  }

  const abortGame = async () => {
    if (game.status === 'pending') {
      await abortLooking(game.gameId)
    } else if (game.status === 'playing') {
      await finishGameByAbort(game.gameId, playerColor.top)
    }
    navigate('/')
  }

  return (
    <div>
      <div className="absolute inset-0 bg-[#121212]" />
      <div
        className="relative mx-auto my-0 flex h-screen w-screen flex-col bg-cover bg-center lg:aspect-[3/4] lg:max-w-[1000px]"
        style={{ backgroundImage: `url(${gameBg})` }}
      >
        <EndingDialog />
        <RespondDrawDialog />
        <div className="m-0 flex justify-between sm:m-2">
          <Dialog>
            {['pending', 'playing'].includes(game.status) ? (
              <DialogTrigger asChild>
                <Button variant="ghost">
                  <ArrowLeft />
                  Home
                </Button>
              </DialogTrigger>
            ) : (
              <Button variant="ghost" onClick={() => navigate('/')}>
                <ArrowLeft />
                Home
              </Button>
            )}
            <DialogContent className="w-11/12 max-w-[850px] rounded-xl p-5">
              <DialogTitle>Do you want to abort?</DialogTitle>
              <DialogClose asChild>
                <div className="flex gap-10">
                  <RedButton className="w-full" text="Cancel" />
                  <GreenButton className="w-full" text="Abort" onClick={abortGame} />
                </div>
              </DialogClose>
            </DialogContent>
          </Dialog>
          <Button variant="ghost" onClick={skip}></Button>
          <span className="invisible absolute left-1/2 top-3 -translate-x-1/2 text-2xl italic text-black sm:visible">
            Elementums
          </span>
          <div className="flex gap-0 sm:gap-3">
            <Button variant="ghost" onClick={shareGame} className="flex gap-2">
              <Share2Icon /> Share
            </Button>
            <Button variant="ghost" onClick={copyGameId}>
              Game: {game.gameId}
            </Button>
          </div>
        </div>
        <hr />
        <Header />
        <PlayerHand position="top" playerInfo={topPlayer} />
        <SVGPlane>
          <HexBoard />
        </SVGPlane>
        <PlayerHand position="bottom" playerInfo={bottomPlayer} />
        {phase === 'distribute' && (
          <>
            {crownNextPiece || (!isCrownedPieceOnBoard && hand.bottom.length === 2) ? (
              <GreenButton
                className="mx-auto mb-2 w-4/5 rounded-full p-2 text-2xl sm:p-4"
                text="Crown next pawn"
                iconLeading={<CrownIcon />}
                onClick={handleCrownNextPiece}
              />
            ) : (
              <RedButton
                className="mx-auto mb-2 w-4/5 rounded-full p-2 text-2xl sm:p-4"
                text="Crown next pawn"
                disabled={isCrownedPieceOnBoard || !isGameOpen(game.status)}
                iconLeading={<CrownIcon />}
                onClick={handleCrownNextPiece}
              />
            )}
          </>
        )}
        {phase === 'fight' && (
          <RedButton
            className="mx-auto mb-2 w-4/5 rounded-full p-2 text-2xl sm:p-4"
            text="Fight"
            iconLeading={<FightIcon className="h-6" />}
            disabled={(isTurnProtection && turn.top) || !isGameOpen(game.status)}
            onClick={handleFightClick}
          />
        )}
        {showFightAnimation && (
          <motion.div
            initial={{ opacity: 0, scale: 0, x: '-50%', y: '-50%' }}
            animate={{ opacity: 0.5, scale: [0, 1, 0.8] }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="absolute left-1/2 top-1/2 z-50 text-white"
          >
            <FightIcon className="w-96" />
          </motion.div>
        )}
        <Dialog>
          <DialogTrigger className="mb-3 text-center text-xl font-bold text-white">How to Play</DialogTrigger>
          <DialogContent className="h-4/5 w-11/12 max-w-[850px] p-0">
            <iframe
              className="h-full w-full"
              src="https://docs.google.com/document/d/e/2PACX-1vSV13S3lVcw4m4_DFeVLFrC1RUqGRCrsu26INPjJWmtN5iUlPhm5C2VwobeoSoyAdHrPHYP2Tep3Hp1/pub?embedded=true"
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
